import { RootSelector, getStore } from '../../reducers';
import { ProjectShape, sortProjects } from './model';
import { createSelector } from '@reduxjs/toolkit';
import { Project } from '../../api';
import { getOwnSpaceId } from '../demoWelcome/selectors';

export const getProject: RootSelector<ProjectShape> = createSelector(
  getStore,
  (state) => state.project,
);

// On the Demo instance the user's own "Your space" goes first (getOwnSpaceId is
// null elsewhere, so the order is unchanged there).
export const getProjects: RootSelector<Project[]> = createSelector(
  [getProject, getOwnSpaceId],
  (project, ownSpaceId) => {
    const projects = [...project.projects].sort(sortProjects);
    if (ownSpaceId === null) return projects;
    const own = projects.find((p) => p.id === ownSpaceId);
    return own ? [own, ...projects.filter((p) => p.id !== ownSpaceId)] : projects;
  },
);

export const getProjectsById: RootSelector<Partial<Record<Project['id'], Project>>> =
  createSelector(getProjects, (projects) => {
    const result: Partial<Record<Project['id'], Project>> = {};
    projects.forEach((project) => {
      result[project.id] = project;
    });
    return result;
  });

export const getSelectedIds: RootSelector<Project['id'][]> = createSelector(
  getProject,
  (project) => project.selectedIds,
);

export const getLoading: RootSelector<boolean> = createSelector(
  getProject,
  (project) => project.loading,
);

export const getExpanded = createSelector(getProject, (project) => project.expanded);

export const getProjectsCollapsed = createSelector(
  [getSelectedIds, getProjectsById],
  (selectedIds, projectsById) => {
    if (selectedIds.length === 0) {
      return 'All projects';
    }

    const titles = selectedIds
      .map((id) => projectsById[id])
      .filter((project): project is NonNullable<typeof project> => Boolean(project))
      .sort(sortProjects)
      .map(({ name }) => name);

    const title = titles[0] ?? '';
    const result = `${title}${titles.length > 1 ? ` +${titles.length - 1}` : ''}`;
    return result;
  },
);
